import './revenueDetailsTable.css'

import { CommonAlert } from 'CommonAlert'
import { SORT_ORDER } from 'constants.js'
import noop from 'lodash/noop'
import PropTypes from 'prop-types'
import React, { Component } from 'react'
import Slider from 'Slider/Slider'
import SliderItem from 'Slider/SliderItem'
import { SliderDots } from 'SliderDots'
import { TableClubCard } from 'TableClubCard'
import { TableCommon } from 'TableCommon'
import { TableMargin } from 'TableMargin'
import { TableOrders } from 'TableOrders'
import { TableReplenishments } from 'TableReplenishments'
import { TableTraffic } from 'TableTraffic'
import { TableWeather } from 'TableWeather'
import { emptyArray } from 'utils'

import { ALL_STORES_COUNTRY_ID, dataSourceOptions, DEFAULT_SORT_BY } from './constants'
import RevenueStationaryTable from './RevenueStationaryTable'
import RevenueWebsiteTable from './RevenueWebsiteTable'
import structureCumulative from './structureCumulative'
import structureDataSource from './structureDataSource'
import structureReturns from './structureReturns'

const ITEM_HEIGHT = 41

function getFrameItem(condition, node) {
  if (!condition) {
    return emptyArray
  }

  return [
    {
      node,
    },
  ]
}

class RevenueDetailsTable extends Component {
  state = {
    scrollOffset: 0,
    selectedFrame: 0,
  }

  componentWillUnmount() {
    clearTimeout(this.scrollTimeout)
  }

  countryId = this.props.countryID || ALL_STORES_COUNTRY_ID

  scrollTimeout = 0

  getCountrySortBy = () => ({
    value: this.props.sortBy,
    order: this.props.sortOrder,
  })

  showModal = store => {
    this.props.openModal(store.storeId, this.countryId)
  }

  filterTable = e => {
    e.stopPropagation()

    const sortBy = this.getCountrySortBy()
    const changedSort = sortBy.order === SORT_ORDER.ASC ? SORT_ORDER.DESC : SORT_ORDER.ASC
    const nextOrderSameAsOld = sortBy.value === e.target.dataset.value

    this.props.changeDataSorting(
      {
        sortBy: e.target.dataset.value,
        sortOrder: nextOrderSameAsOld ? changedSort : SORT_ORDER.DESC,
      },
      this.countryId,
    )
  }

  selectFrame = frame => this.setState({ selectedFrame: frame })

  onScroll = ({ scrollOffset }) => {
    if (!this.props.isFetching && this.state.scrollOffset !== scrollOffset) {
      if (this.scrollTimeout) {
        clearTimeout(this.scrollTimeout)
      }

      this.scrollTimeout = setTimeout(() => {
        this.setState({ scrollOffset })
      }, 200)
    }
  }

  render() {
    const {
      contentType,
      displayChannel,
      errorCode,
      fetchStores,
      isCumulative,
      isFetching,
      showClubCardTable,
      showOrders,
      showReplenishmentsTable,
      sortBy,
      sortOrder,
      stores,
      type,
      userPrivileges,
    } = this.props
    const { scrollOffset } = this.state
    const listHeight = Math.min(ITEM_HEIGHT * 10, stores.length * ITEM_HEIGHT)
    const maxRevenue = Math.max(...stores.map(store => store.revenueCurrentPLN))
    const maxRevenueClub = Math.max(...stores.map(store => store.revenueClubCurrent))
    const maxOrdersValue = Math.max(...stores.map(store => store.ordersValue))
    const maxTraffic = Math.max(...stores.map(store => store.trafficCurrent || 0))
    const maxReplenishments = Math.max(...stores.map(store => store.replenishmentOnHandShoesCurrent))
    const totalRevenue = stores.length && stores.reduce((prev, cur) => prev + cur.revenueCurrentPLN, 0)
    const totalOrders = stores.length && stores.reduce((prev, cur) => prev + cur.orders, 0)
    const totalOrdersValue = stores.length && stores.reduce((prev, cur) => prev + cur.ordersValue, 0)
    const isWebsite = contentType === 'websites'
    const RevenueTable = isWebsite ? RevenueWebsiteTable : RevenueStationaryTable
    const showOrdersTable = isWebsite && showOrders
    const tableProps = {
      headerClassName: `table-${type}-header`,
      displayChannel,
      itemHeight: ITEM_HEIGHT,
      listHeight,
      onFilter: this.filterTable,
      onScroll: this.onScroll,
      scrollOffset,
      showModal: this.showModal,
      sortBy: {
        value: sortBy,
        order: sortOrder,
      },
      stores,
      type,
    }
    const dataSources = dataSourceOptions.map(({ key, description }, index) => (
      <div key={`data-source-${key.toLowerCase()}`} className="revenue-table_data-source-value">
        {key}: {description}
        {index + 1 < dataSourceOptions.length && ','}
      </div>
    ))
    const frames = [
      ...getFrameItem(
        userPrivileges.rangesVisibility,
        <SliderItem key="TableCumulative">
          <TableCommon {...tableProps} maxRevenue={maxRevenue} structure={structureCumulative} wrapperClass="table-cumulative" />
        </SliderItem>,
      ),
      ...getFrameItem(
        userPrivileges.marginVisibility,
        <SliderItem key="TableMargin">
          <TableMargin {...tableProps} maxRevenue={maxRevenue} />
        </SliderItem>,
      ),
      ...getFrameItem(
        !isWebsite,
        <SliderItem key="TableWeather">
          <TableWeather {...tableProps} maxRevenue={maxRevenue} />
        </SliderItem>,
      ),
      ...getFrameItem(
        !isWebsite,
        <SliderItem key="TableTraffic">
          <TableTraffic {...tableProps} maxTraffic={maxTraffic} />
        </SliderItem>,
      ),
      ...getFrameItem(
        showClubCardTable,
        <SliderItem key="TableClubCard">
          <TableClubCard {...tableProps} maxRevenueClub={maxRevenueClub} />
        </SliderItem>,
      ),
      ...getFrameItem(
        showReplenishmentsTable && !isWebsite,
        <SliderItem key="TableReplenishments">
          <TableReplenishments {...tableProps} maxReplenishments={maxReplenishments} />
        </SliderItem>,
      ),
      {
        node: (
          <SliderItem key="RevenueTable">
            <RevenueTable
              {...tableProps}
              dataSourceVisibility={userPrivileges.dataSourceVisibility}
              maxRevenue={maxRevenue}
              totalRevenue={totalRevenue}
            />
          </SliderItem>
        ),
      },
      ...getFrameItem(
        showOrdersTable,
        <SliderItem key="TableOrders">
          <TableOrders
            {...tableProps}
            maxOrdersValue={maxOrdersValue}
            totalOrders={totalOrders}
            totalOrdersValue={totalOrdersValue}
          />
        </SliderItem>,
      ),
      {
        node: (
          <SliderItem key="TableReturns">
            <TableCommon {...tableProps} maxRevenue={maxRevenue} structure={structureReturns} wrapperClass="table-returns" />
          </SliderItem>
        ),
      },
      ...getFrameItem(
        userPrivileges.dataSourceVisibility,
        <SliderItem key="TableDataSource">
          <TableCommon
            {...tableProps}
            shareData={{ isCumulative }}
            maxRevenue={maxRevenue}
            structure={structureDataSource}
            wrapperClass="table-data-source"
          />
          <div className="revenue-table_data-source">
            <i className="fm fm-database" />
            <div className="revenue-table_data-source-wrapper">{dataSources}</div>
          </div>
        </SliderItem>,
      ),
    ]

    return (
      <>
        {Boolean(errorCode) && <CommonAlert errorCode={errorCode} onRefresh={fetchStores} padding="1.4rem 1.6rem 1.2rem 1.6rem" />}
        {!isFetching && !Boolean(errorCode) && (
          <>
            <Slider currentSlideIndex={this.state.selectedFrame} onChange={this.selectFrame}>
              {frames.map(frame => frame.node)}
            </Slider>
            <SliderDots
              className="tables-slider-dots"
              length={frames.length}
              selected={this.state.selectedFrame}
              onChange={this.selectFrame}
            />
          </>
        )}
      </>
    )
  }
}

RevenueDetailsTable.propTypes = {
  changeDataSorting: PropTypes.func,
  contentType: PropTypes.string.isRequired,
  countryID: PropTypes.string,
  displayChannel: PropTypes.bool,
  errorCode: PropTypes.number,
  fetchStores: PropTypes.func,
  isCumulative: PropTypes.bool.isRequired,
  isFetching: PropTypes.bool,
  openModal: PropTypes.func,
  showClubCardTable: PropTypes.bool.isRequired,
  showOrders: PropTypes.bool.isRequired,
  showReplenishmentsTable: PropTypes.bool.isRequired,
  sortBy: PropTypes.string,
  sortDirection: PropTypes.string,
  stores: PropTypes.array,
  type: PropTypes.string,
  userPrivileges: PropTypes.object.isRequired,
}

RevenueDetailsTable.defaultProps = {
  openModal: noop,
}

export default RevenueDetailsTable
